import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { ShortUrl } from '@air/api';
import { type Board, type Clip, type Form, type ListResponse, type ShortUrlResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { removeShortUrlFromListCache } from '~/components/ShowcaseModal/hooks/mutations/utils';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getShortIdInformationKey } from './useGetExistingShortIdInformation';
import { isShortUrlsListKey } from './useListShortUrls';

export const useDeleteShortId = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const deleteShortId = useMutation({
    mutationFn: ({
      shortId,
      boardContext,
    }: Parameters<typeof ShortUrl.deleteByShortId>[0] & {
      id: Board['id'] | Clip['id'] | Form['id'];
      boardContext: Board['id'] | undefined;
    }) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      return ShortUrl.deleteByShortId({ workspaceId, shortId, boardContext });
    },
    onMutate: ({ id, boardContext }) => {
      queryClient.setQueryData(getShortIdInformationKey({ objectId: id, boardContext }), null);
    },
    onSuccess: (_data, { id, boardContext, workspaceId, shortId }) => {
      queryClient.invalidateQueries({ queryKey: getShortIdInformationKey({ objectId: id, boardContext }) });

      queryClient.setQueriesData<InfiniteData<ListResponse<ShortUrlResponse>> | undefined>(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return isShortUrlsListKey(key) && key[1].workspaceId === workspaceId;
          },
          exact: false,
        },
        (old) => (old ? removeShortUrlFromListCache(shortId, old) : old),
      );
    },
    onError: (error, params) => {
      throw reportErrorToBugsnag({
        error,
        context: 'Failed to delete short id',
        metadata: { data: params },
      });
    },
  });

  return {
    deleteShortId,
  };
};
